import { useQuery, UseQueryResult } from "@tanstack/react-query";
import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import qs from "qs";


const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface MessageReSubmissionType {
  id: string;
  name: string;
  status: string;
  dlrStatus: string;
  operatorId: number;
  fromDate: Date;
  isSubmitted: boolean;
  resubmissionStatus: string;
  toDate: Date;
  providerId: string;
  customerId: string;
  createdDate: string;
  postTranslatedSource: string;
  version: number;
  createdBy: string;
  historyTimestamp: Date;
  updatedBy?: string;
}

export interface MessageReSubmissionHistory {
  id: string;
  snapshot: MessageReSubmissionType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}


export enum Status {
  PENDING,
  DONE,
  FAILED,
}
export enum DlrStatus {
  ENROUTE,
  DELIVRD,
  EXPIRED,
  DELETED,
  UNDELIV,
  ACCEPTD,
  UNKNOWN,
  REJECTD,
}

export interface MessageReSubmissionSummary {
  count: number;
  delivered: number;
  totalCost: number;
  totalRate: number;
}

export const exportMessageReSubmission = async (
  params: Params<MessageReSubmissionType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/messages/resubmissions/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "MessageReSubmission.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching MessageReSubmission :",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchMessageReSubmissions(
  params: Params<MessageReSubmissionType>
): Promise<IPageable<MessageReSubmissionType>> {
  try {
    const response = await axiosInstance.get("/messages/resubmissions", {
      params: { ...params, sort: "createdDate,desc" },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}
export async function CheckMessageReSubmissionAPI(
  params: Params<MessageReSubmissionType>
): Promise<MessageReSubmissionType> {
  try {
    const response = await axiosInstance.get("/messages/resubmissions/stats", {
      params,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}

export async function fetchMessageReSubmission(
  id: MessageReSubmissionType["id"]
): Promise<MessageReSubmissionType> {
  try {
    const response = await axiosInstance.get(`/messages/resubmissions/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting Message re-Submission/${id}.`
    );
  }
}

export const useMessageReSubmissionQuery = (
  id: MessageReSubmissionType["id"]
): UseQueryResult<MessageReSubmissionType, Error> => {
  const queryKey = ["messageRe", JSON.stringify(id)];
  return useQuery({
    queryKey,
    queryFn: () => fetchMessageReSubmission(id),
    enabled: Boolean(id),
    // Poll every second only while the item is in PENDING state
    refetchInterval: (query) => {
      // query is a TanStack Query instance; use its state to inspect the latest data
      const status = (query as any)?.state?.data?.resubmissionStatus;
      return status === "PENDING" ? 1000 : false;
    },
  });
};


export const addMessageReSubmissionApi = async (
  data: Partial<MessageReSubmissionType>
) => {
  try {
    const response = await axiosInstance.post("/messages/resubmissions", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Message re-Submission."
    );
  }
};

export async function editMessageReSubmissionApi(
  id: string,
  data: Partial<MessageReSubmissionType>
): Promise<MessageReSubmissionType> {
  try {
    const response = await axiosInstance.put(
      `/messages/resubmissions/${id}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while updating messages/resubmissions/${data.id}.`
    );
  }
}

export const deleteMessageReSubmissionsApi = async (
  ids: string[],
): Promise<void> => {
  try {
    await axiosInstance.delete("/messages/resubmissions", { data: ids });
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the Message re-Submission(s).",
    );
  }
};

export const getMessageReSubmissionById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/messages/resubmissions/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the Message re-Submission."
    );
  }
};

export const addProvider = async (id: string, providerId: string) => {
  try {
    const response = await axiosInstance.post(
      `/messages/resubmissions/${id}/execute`,
      null,
      {
        params: { vendorId: providerId },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the Message re-Submission."
    );
  }
};

export const useMessageReSubmissionsQuery = (
  params: Params<MessageReSubmissionType>
): UseQueryResult<IPageable<MessageReSubmissionType>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["messageRe", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchMessageReSubmissions(effectiveParams),
  });
};

export const messageReSubmissionBulkApi = async (
  data: Partial<MessageReSubmissionType>[]
) => {
  try {
     const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.put("/messages/resubmissions", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the re-Submission."
    );
  }
};

export async function fetchMessageReSubmissionHistory(
  id: MessageReSubmissionType["id"],
  params: Params<MessageReSubmissionHistory>
): Promise<IPageable<MessageReSubmissionType>> {
  try {
    const response = await axiosInstance.get(
      `/messages/resubmissions/history`, { params: { mainId: id , sort: "historyTimestamp,desc", ...params } }
    );
    const data = response.data;
  return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the FallBack Routing history for ID ${id}.`
    );
  }
}

